import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from 'react';

import Observer from '@researchgate/react-intersection-observer';
import config from 'config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes, { oneOfType } from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import FitGuarantee from '~/acquisition/components/FitGuarantee';
import CmsStorytelling from '~/builder/components/CmsStorytelling';
import PDPVideoCarousel from '~/builder/components/PDPVideoCarousel';
import { BUILDER_MODELS } from '~/builder/constants/models';
import useBuilderJson from '~/builder/utils/useBuilderJson';
import CanonicalLink from '~/layout/components/CanonicalLink';
import ModelImageProviderWrapper from '~/pdp/components/ModelImageProviderWrapper';
import PDPProductLoading from '~/pdp/components/PDPProductLoading';
import { ProductDetailImagesLoading } from '~/pdp/components/ProductDetailImages';
import { ProductDetailImagesGridLoading } from '~/pdp/components/ProductDetailImagesGrid';
import { useAssortmentTest } from '~/pdp/context/AssortmentTestContext';
import { prioritizeRelatedSKUs } from '~/pdp/utils/prioritizeRelatedSKUs';
import useProfileSizes from '~/pdp/utils/useProfileSizes';
import ContentArea from '~/shared/components/ContentArea';
import ScrollOffset from '~/shared/components/ScrollOffset';
import featureNames from '~/shared/constants/featureFlags';
import { productDetailPath } from '~/shared/constants/product';
import { cookieSizeMap } from '~/shared/constants/productSizes';
import { getIsPlusSizeByField } from '~/shared/utils/getIsPlusSizeByField';
import { getSizesFromCookies } from '~/shared/utils/getSizesFromCookies';
import { isForcedSoldOut } from '~/shared/utils/isForcedSoldOut';
import logger from '~/shared/utils/logger';
import { useBrandDomainCanonicalLink } from '~/shared/utils/useBrandDomainCanonicalLink';
import { useBuilderSizeGuide } from '~/shared/utils/useBuilderSizeGuide';
import useEmbroideryConfigs from '~/shared/utils/useEmbroideryConfigs';
import useGender from '~/shared/utils/useGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import useTldLocale from '~/shared/utils/useTldLocale';
import { useIsPostReg, useMembership } from '~/techstyle-shared/react-accounts';
import {
  mobile,
  Separator,
  ProductAttributeField,
  ProductDetail,
  ProductDetailSchema,
  ReviewsDrawerProvider,
  useProductDetailContext,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { useIntl, useCurrency } from '~/techstyle-shared/react-intl';
import { ProductType } from '~/techstyle-shared/react-products';
import {
  buildURL,
  useCookies,
  useSessionActions,
} from '~/techstyle-shared/redux-core';

import PDPMainContent from '../PDPMainContent';

const debug = logger.extend('PDP');

if (process.browser) {
  require('intersection-observer');
}
const UpsellModule = dynamic(
  () =>
    import(
      /* webpackChunkName: "UpsellModule" */ '~/pdp/components/UpsellModule'
    ),
  { ssr: false }
);
const ReviewsSummarySection = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReviewsSummarySection" */ '~/pdp/components/ReviewsSummarySection'
    ),
  { ssr: false }
);
const ReviewsPhotoDrawer = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReviewsPhotoDrawer" */ '~/pdp/components/ReviewsPhotoDrawer'
    ),
  { ssr: false }
);
const ReviewsDrawer = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReviewsDrawer" */ '~/pdp/components/ReviewsDrawer'
    ),
  { ssr: false }
);
const OOSCarousel = dynamic(
  () =>
    import(
      /* webpackChunkName: "OOSCarousel" */ '~/pdp/components/OOSCarousel'
    ),
  { ssr: false }
);

const ProductDetailNotFoundModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ProductDetailNotFoundModal" */ '~/pdp/components/ProductDetailNotFoundModal'
    )
);

const ProductContentWrapper = styled.main`
  margin: 32px auto 0;

  ${mobile`
    margin-top: 0;
  `};
`;

const separatorStyle = css`
  color: ${({ theme }) => theme.colors.separator};
  margin-block: 14px 16px;
`;

const ReviewsWrapper = styled.div`
  ${({ theme }) => theme.reviewsSummaryWrapper};
`;

const GridContentArea = styled(ContentArea)`
  max-width: 1327px;

  @media (max-width: 1327px) {
    padding-inline: 32px;
  }

  @media (min-width: ${({ theme }) =>
      theme.breakpoints.tablet.minWidth}px) and (max-width: ${({ theme }) =>
      theme.breakpoints.tablet.maxWidth}px) {
    max-width: 100%;
  }
`;

function OOSCarouselWrapper() {
  const { enabled: showOOS } = useHasFeature(featureNames.SHOW_OOS);
  const { isOutOfStock, isPreOrderOutOfStock } = useProductDetailContext();

  return isOutOfStock && isPreOrderOutOfStock && !showOOS && <OOSCarousel />;
}

const PDPContent = ({
  builderQueries,
  permalink,
  product,
  relatedProductsLoading,
  baseProductLoading,
  productError,
  isAssortmentTestProductDisabled,
}) => {
  const reviewSectionRef = useRef();
  const { isFemale } = useGender();
  const { currency } = useCurrency();
  const router = useRouter();
  const canonicalUrl = useBrandDomainCanonicalLink();
  const brand = useSelector((state) => state.domain.name);
  const { isMobile } = useBreakpoint();
  const secondarySite = useSecondarySite();
  const routePrefix = secondarySite ? `/${secondarySite}` : '';
  const embroideryConfigs = useEmbroideryConfigs();

  const builderSizeGuides = useBuilderJson({
    modelName: BUILDER_MODELS.SIZE_GUIDE,
    options: { getAll: true },
  });

  const sizeGuides = useMemo(() => {
    return builderSizeGuides?.builderJson || {};
  }, [builderSizeGuides]);

  const matchedBuilderSizeGuide = useBuilderSizeGuide({
    sizeGuides,
    categoryIdList: product?.productCategoryIdList,
    defaultCategoryId: product?.defaultCategoryId,
    tagIdList: product?.tagIdList,
  });

  const membership = useMembership();
  const isVip = membership.isVip;
  const isPostReg = useIsPostReg();
  const currentUserStatus = useMemo(
    () => ({ ...membership, isPostReg }),
    [membership, isPostReg]
  );
  const { formatMessage } = useIntl();
  const tldLocale = useTldLocale();
  const sessionActions = useSessionActions();
  const pdpViewSessionDetailName = formatMessage({
    id: 'global_cta.pdp_detail_record',
    defaultMessage: 'pdp',
  });

  const [cookies] = useCookies(Object.keys(cookieSizeMap));
  const cookieSizes = useMemo(
    () => getSizesFromCookies({ cookies, isPdp: true, tldLocale }),
    [cookies, tldLocale]
  );

  const getIsForcedSoldOut = useCallback(
    (product) => {
      return isForcedSoldOut(
        product,
        isVip,
        isFemale,
        isAssortmentTestProductDisabled
      );
    },
    [isFemale, isVip, isAssortmentTestProductDisabled]
  );

  const { profileSizes } = useProfileSizes();

  const { registerDisabledProduct, isProductDisabled } = useAssortmentTest();

  // Register the main product if disabled (persist across navigation)
  useEffect(() => {
    if (product?.masterProductId && isAssortmentTestProductDisabled) {
      registerDisabledProduct(product.masterProductId);
    }
  }, [product, isAssortmentTestProductDisabled, registerDisabledProduct]);

  let content;

  const mensShortsId = config.get('public.productCategories.mensShort');
  const mensUnderwearId = config.get('public.productCategories.mensUnderwear');

  const isMensShorts = product?.productCategoryIdList?.includes(mensShortsId);
  const isMensUnderwear =
    product?.productCategoryIdList?.includes(mensUnderwearId);

  const getPreselectAttributeIndex = useCallback(
    (attribute) => {
      if (
        attribute?.field === ProductAttributeField.INSEAM &&
        attribute?.options?.length > 1 &&
        (isMensShorts || isMensUnderwear)
      ) {
        return 1;
      }
      return null;
    },
    [isMensShorts, isMensUnderwear]
  );

  useEffect(() => {
    sessionActions.updateSessionDetail({
      name: pdpViewSessionDetailName,
      value: 'react',
    });
  }, [sessionActions, pdpViewSessionDetailName]);

  useEffect(() => {
    if (baseProductLoading) {
      const timeout = setTimeout(() => {
        window.scrollTo(0, 0);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [baseProductLoading]);

  const [isReviewSectionActive, setIsReviewSectionActive] = useState(false);
  const handleReviewVisibility = (entry, unobserve) => {
    if (entry.isIntersecting) {
      setIsReviewSectionActive(true);
      unobserve();
    }
  };

  let ProductContentArea = ContentArea;
  let ProductImagesLoading = ProductDetailImagesLoading;

  if (!isMobile) {
    ProductContentArea = GridContentArea;
    ProductImagesLoading = ProductDetailImagesGridLoading;
  }

  const hasWearItWithProductIds = product?.wearItWithProductIds?.length > 0;
  const canDisplayWearItWith = hasWearItWithProductIds;
  const isBundle = product?.productTypeId === ProductType.BUNDLE;
  const isMysteryBox = product?.isMysteryBundle;

  const buildProductURL = useCallback(
    (permalink) => {
      const { query } = router;
      return buildURL(`${routePrefix}/${productDetailPath}/${permalink}`, {
        ...query,
        identifier: null,
      });
    },
    [router, routePrefix]
  );

  const handleRedirect = useCallback(
    (permalink, forceHardReload = false) => {
      const newURL = buildProductURL(permalink);

      if (forceHardReload) {
        window.location.href = newURL;
        return;
      }

      const { query } = router;
      const pathname = `/${productDetailPath}/[identifier]`;
      router.replace(
        {
          pathname,
          query,
        },
        newURL,
        {
          scroll: false,
        }
      );
    },
    [router, buildProductURL]
  );

  const handlePermalinkChange = useCallback(
    (newPermalink) => {
      if (permalink !== newPermalink) {
        handleRedirect(newPermalink);
      }
    },
    [permalink, handleRedirect]
  );

  const handleColorChange = useCallback(
    ({ option: colorOption, relatedSKUs, selectionStateByProductIndex }) => {
      // bundles should not react to selector changes for permalink updates
      if (isBundle) {
        return;
      }
      const [selectionState] = selectionStateByProductIndex;
      const { selectedSKU, selectedAttributes } = selectionState;
      const [newSKU] = prioritizeRelatedSKUs({
        skus: relatedSKUs,
        selectedAttributes,
      });

      debug('prioritizeRelatedSKUs', {
        newSKU,
        skus: relatedSKUs,
        selectedAttributes,
      });

      if (newSKU && permalink !== newSKU.permalink) {
        // If current product is assortment test product disabled, force a hard reload
        handleRedirect(newSKU.permalink, isAssortmentTestProductDisabled);
        // this line is here to prevent the handlePermalinkChange from executing
        // on the same render. this prevents us from navigating twice and incurring
        // a duplicate render
        // eslint-disable-next-line react-hooks/exhaustive-deps
        permalink = newSKU.permalink;
      }

      debug('onColorChange', {
        colorOption,
        newSKU,
        relatedSKUs,
        selectedSKU,
        selectionState,
      });
    },
    [isBundle, permalink, handleRedirect, isAssortmentTestProductDisabled]
  );

  // for the else if case, even if we have product data, if we receive
  // an error we're going to consider the product not found right now.
  // the case we know about and should optimize is that a related product
  // may be broken and cause an error to throw. if we fix the gql side
  // to return partial data then maybe we can compare the swatches to the
  // returned related products and hide the swatches without a corresponding
  // related product so we don't have to hide products that are actually
  // available here
  if (baseProductLoading) {
    content = (
      <PDPProductLoading
        key="loading"
        productImagesLoading={ProductImagesLoading}
        contentArea={ProductContentArea}
      />
    );
  } else if (productError && !product) {
    debug(productError);

    content = (
      <ProductContentWrapper>
        <ProductDetailNotFoundModal />
      </ProductContentWrapper>
    );
  } else {
    const showProductRating = product?.reviewCount > 0;

    const showFitGuarantee =
      !secondarySite &&
      !isMysteryBox &&
      config.get('public.brand.features.fitGuarantee');

    content = (
      <>
        <CanonicalLink />
        <Head>
          <link rel="canonical" href={canonicalUrl} />
        </Head>
        <ProductDetail
          product={product}
          onPermalinkChange={handlePermalinkChange}
          onColorChange={handleColorChange}
          profileSizes={profileSizes}
          sessionSizes={cookieSizes}
          getIsPlusSize={getIsPlusSizeByField}
          getIsForcedSoldOut={getIsForcedSoldOut}
          currentUserStatus={currentUserStatus}
          getPreselectAttributeIndex={getPreselectAttributeIndex}
          allowInvalidOptionSelection
          isAssortmentTestProductDisabled={isAssortmentTestProductDisabled}
          isProductDisabled={isProductDisabled}
          embroideryConfigs={embroideryConfigs}
          builderSizeGuide={matchedBuilderSizeGuide?.builderContent}
          sizeGuides={sizeGuides}
        >
          <ProductDetailSchema
            brand={brand}
            canonicalUrl={canonicalUrl}
            currency={currency}
          />
          <ReviewsDrawerProvider>
            <ProductContentWrapper>
              <ProductContentArea>
                <OOSCarouselWrapper />
                <PDPMainContent
                  isVip={isVip}
                  product={product}
                  relatedProductsLoading={relatedProductsLoading}
                  builderQueries={builderQueries}
                  reviewSectionRef={reviewSectionRef}
                  isAssortmentTestProductDisabled={
                    isAssortmentTestProductDisabled
                  }
                />
              </ProductContentArea>
              {!secondarySite && !isMysteryBox && (
                <>
                  <PDPVideoCarousel
                    product={product}
                    builderQueries={builderQueries}
                  />
                  <ProductContentArea>
                    {!canDisplayWearItWith && (
                      <Separator
                        thickness={1}
                        separatorStyle={separatorStyle}
                      />
                    )}
                    <ModelImageProviderWrapper profileSizes={profileSizes}>
                      <UpsellModule wearItWith youWillAlsoLove={false} />
                    </ModelImageProviderWrapper>
                    <CmsStorytelling
                      product={product}
                      builderQueries={builderQueries}
                    />
                    <ModelImageProviderWrapper profileSizes={profileSizes}>
                      <UpsellModule wearItWith={false} youWillAlsoLove />
                    </ModelImageProviderWrapper>
                  </ProductContentArea>
                </>
              )}
              {!isBundle && showProductRating && (
                <Observer
                  onChange={handleReviewVisibility}
                  disabled={isReviewSectionActive}
                >
                  <ScrollOffset ref={reviewSectionRef}>
                    {isReviewSectionActive && (
                      <ReviewsWrapper
                        id="reviews"
                        data-testid="pdp-reviews-section"
                      >
                        <ReviewsSummarySection />
                      </ReviewsWrapper>
                    )}
                  </ScrollOffset>
                </Observer>
              )}
            </ProductContentWrapper>
            <ReviewsPhotoDrawer />
            <ReviewsDrawer />
          </ReviewsDrawerProvider>
          {showFitGuarantee && <FitGuarantee />}
        </ProductDetail>
      </>
    );
  }

  return content;
};

PDPContent.propTypes = {
  baseProductLoading: PropTypes.bool,
  builderQueries: PropTypes.object,
  isAssortmentTestProductDisabled: PropTypes.bool,
  mysteryBoxConfig: PropTypes.object,
  permalink: PropTypes.string,
  product: PropTypes.object,
  productError: oneOfType([PropTypes.object, PropTypes.bool]),
  relatedProductsLoading: PropTypes.bool,
};

export default PDPContent;
